import { useState } from 'react'
import { Modal, PasswordInput, Group, Stack } from '@mantine/core'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/Button'
import type { ManagedStudent } from 'api/arcMosaico/types'

interface Props {
  student: ManagedStudent | null
  onClose: () => void
  onSave: (password: string) => Promise<void>
}

export function ResetPasswordModal({ student, onClose, onSave }: Props) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const opened = student !== null

  const handleClose = () => {
    setPassword('')
    setConfirmPassword('')
    setError('')
    onClose()
  }

  const handleSave = async () => {
    if (password.length < 6) {
      setError(t('reset_password_modal.min_length'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('reset_password_modal.mismatch'))
      return
    }
    setError('')
    setLoading(true)
    try {
      await onSave(password)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('reset_password_modal.title')}
      size='sm'
      centered
    >
      <Stack spacing='sm'>
        <PasswordInput
          label={t('reset_password_modal.password')}
          value={password}
          onChange={e => setPassword(e.currentTarget.value)}
          required
          error={error && password.length < 6 ? error : undefined}
        />
        <PasswordInput
          label={t('reset_password_modal.confirm_password')}
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.currentTarget.value)}
          required
          error={error && password !== confirmPassword ? error : undefined}
        />

        <Group position='right' mt='md'>
          <Button variant='default' onClick={handleClose}>
            {t('reset_password_modal.cancel')}
          </Button>
          <Button
            color='turquoise'
            leftIcon={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={loading}
          >
            {t('reset_password_modal.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
